import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import DiaRecorrido from './DiaRecorrido';
import DecisionMes from './DecisionMes';
import { Kpi, Aviso, Plata, pesos, km, horas, fechaLarga } from './comun';

// El tramo se aplica a TODOS los equipos del mes: pasar el límite puede hacer cobrar menos.
function avisoTramo(d) {
    const { tramos = [], tramoAplicado: act, siguienteTramo: sig, equipos } = d;
    if (act && sig) {
        const hoyMax = (sig.desde - 1) * act.precio;
        const recupera = Math.ceil(hoyMax / sig.precio);
        if (recupera > sig.desde) {
            return `Ojo con el tramo: si llegás a ${sig.desde} equipos el precio baja a ${pesos(sig.precio)} para todos. `
                + `Con ${sig.desde} cobrarías ${pesos(sig.desde * sig.precio)} + IVA, menos que con ${sig.desde - 1} (${pesos(hoyMax)} + IVA). `
                + `Recién con ${recupera} equipos cobrás más.`;
        }
    }
    const i = tramos.findIndex(t => act && t.desde === act.desde);
    if (i > 0) {
        const ant = tramos[i - 1];
        const conAnterior = (act.desde - 1) * ant.precio;
        if (equipos * act.precio < conAnterior) {
            return `Con ${equipos} equipos cobrás ${pesos(equipos * act.precio)} + IVA: menos que con ${act.desde - 1} (${pesos(conAnterior)} + IVA). `
                + `Hacen falta ${Math.ceil(conAnterior / act.precio)} para que el tramo te convenga.`;
        }
    }
    return null;
}

// 1-nov-2026 (Lucas: "es muchísima info"): arriba lo que te queda; tres números; si hacés lo agendado, con
// una barra al tramo; el día por día en una tabla (tocás un día y se abre); lo demás plegado. El aviso del
// tramo solo cuando estás cerca o cuando de verdad conviene hacer algo distinto.
function FilaDia({ dia, base, precio }) {
    const [abierto, setAbierto] = useState(false);
    const h = dia.horasReales ?? dia.horasEstimadas ?? (dia.recorrido?.minutosTotal || 0) / 60;
    const celda = 'py-2.5 text-caption';
    return (
        <>
            <button type="button" onClick={() => setAbierto(a => !a)} aria-expanded={abierto}
                className="w-full grid grid-cols-[1.2fr_.7fr_1fr_1.1fr_1.2fr] gap-2 px-3 text-left border-t border-black/[0.06] dark:border-white/[0.06] active:bg-panel">
                <span className={`${celda} font-bold text-ink`}>{fechaLarga(dia.fecha).replace(/ de \w+$/, '')}</span>
                <span className={`${celda} text-ink`}>{dia.equipos}</span>
                <span className={`${celda} text-secondary`}>{horas(h)}{dia.horasReales == null ? '*' : ''}</span>
                <span className={`${celda} text-secondary`}>{dia.porHora ? <Plata v={dia.porHora} /> : '—'}</span>
                <span className={`${celda} text-right font-black text-ink`}><Plata v={dia.ganancia} /></span>
            </button>
            {abierto && <div className="px-2 pb-2"><DiaRecorrido dia={dia} base={base} precio={precio} abiertoInicial /></div>}
        </>
    );
}

export default function TabMes({ datos: d }) {
    const [masNumeros, setMasNumeros] = useState(false);
    const plazo = { normal: d.config.plazoNormalDias, urgente: d.config.plazoUrgenteDias };
    const dec = d.decision;
    if (!d.dias.length) {
        return (
            <div className="space-y-3">
                {dec && <DecisionMes d={dec} plazo={plazo} />}
                <p className="py-10 text-center text-muted rounded-2xl bg-panel">No hay equipos atendidos de {d.cliente.nombre} en este período.</p>
            </div>
        );
    }
    const aviso = avisoTramo(d);
    const grafico = d.dias.map(x => ({ dia: fechaLarga(x.fecha).replace('.', ''), equipos: x.equipos, km: x.recorrido.km }));
    const precio = d.conTarifa ? d.precioUnitario : null;
    // Lo agendado: lo que ya tiene día más los pedidos que hay que hacer en el período
    const agendados = dec ? (dec.agendados || 0) + (dec.obligatorios || 0) : 0;
    const cierre = dec?.recomendado;
    // Hay que hacer algo distinto (pasar trabajos al período que viene, o quedás donde se cobra menos)
    const hayQueDecidir = !!(dec && (dec.pasarAlProximo > 0 || dec.quedaEnZona));
    const sig = d.siguienteTramo;
    const cerca = sig && cierre && sig.desde - cierre.equipos <= 3;
    const pct = sig && cierre ? Math.min(100, Math.round((cierre.equipos / sig.desde) * 100)) : null;

    return (
        <div className="space-y-3">
            <Kpi titulo="Te queda" valor={pesos(d.ganancia)} plata tono="bien"
                sub={`Lo que cobrás sin IVA, menos ${[d.tecnicos > 0 && `${pesos(d.tecnicos)} de técnicos`, d.impuestos > 0 && `${pesos(d.impuestos)} de impuestos`, d.pasajes > 0 && `${pesos(d.pasajes)} de pasajes`].filter(Boolean).join(', ') || 'nada'}`} />
            <div className="grid grid-cols-3 gap-2">
                <Kpi titulo="Hechos" valor={d.equipos} sub={precio ? `a ${pesos(precio)}` : 'en el período'} />
                <Kpi titulo="Agendados" valor={agendados} sub={dec && dec.obligatorios ? `${dec.agendados || 0} con día + ${dec.obligatorios} pedidos` : 'con día'} />
                {d.conTarifa
                    ? <Kpi titulo="A cobrar" valor={pesos(d.aFacturar)} plata sub="con IVA" />
                    : <Kpi titulo="Cobrado" valor={pesos(d.ingreso)} plata sub="sin IVA" />}
            </div>

            {cierre && d.conTarifa && (
                <div className={`p-3.5 rounded-2xl border ${hayQueDecidir ? 'border-[color:var(--warning-tx)] bg-[var(--warning-bg)]' : 'border-black/[0.07] dark:border-white/[0.07] bg-card'}`}>
                    <p className="text-label font-black uppercase tracking-widest text-muted">Si hacés lo agendado</p>
                    <p className="mt-1 text-body-lg font-black text-ink">{cierre.equipos} equipos · <Plata v={cierre.cobras} /> + IVA</p>
                    {pct != null && (
                        <>
                            <div className="mt-2 h-2 rounded-full bg-chip overflow-hidden"><div className="h-full bg-[color:var(--etapa-listo)]" style={{ width: `${pct}%` }} /></div>
                            <p className="mt-1 text-caption text-muted">Faltan {Math.max(0, sig.desde - cierre.equipos)} para el tramo de {sig.desde} (ahí pasa a {pesos(sig.precio)} todos)</p>
                        </>
                    )}
                    {hayQueDecidir && <div className="mt-2"><DecisionMes d={dec} plazo={plazo} /></div>}
                    {!hayQueDecidir && cerca && aviso && <p className="mt-2 text-caption font-bold text-[var(--warning-tx)]">{aviso}</p>}
                </div>
            )}

            <p className="pt-1 text-label font-black uppercase tracking-widest text-muted">Día por día</p>
            <div className="rounded-2xl bg-card border border-black/[0.07] dark:border-white/[0.07] overflow-hidden">
                <div className="grid grid-cols-[1.2fr_.7fr_1fr_1.1fr_1.2fr] gap-2 px-3 py-2 text-label font-black uppercase tracking-wider text-muted">
                    <span>Día</span><span>Eq.</span><span>Horas</span><span>Por hora</span><span className="text-right">Te queda</span>
                </div>
                {[...d.dias].reverse().map(x => <FilaDia key={x.fecha} dia={x} base={d.config.base} precio={precio} />)}
            </div>
            <p className="text-caption text-muted">Tocá un día para ver el recorrido. * horas estimadas (no se marcó "Salí" y "Terminé").</p>

            <button type="button" onClick={() => setMasNumeros(v => !v)}
                className="w-full h-11 px-4 rounded-xl border border-black/10 dark:border-white/10 text-label font-black text-secondary flex items-center justify-between active:scale-[0.99]">
                <span>{masNumeros ? 'Menos números' : 'Más números: tramos, ritmo, pasajes, gráfico'}</span><span>{masNumeros ? '▴' : '▾'}</span>
            </button>
            {masNumeros && (
                <div className="space-y-3">
                    {dec && !hayQueDecidir && <DecisionMes d={dec} plazo={plazo} />}
                    {aviso && !cerca && <Aviso tono="alerta">{aviso}</Aviso>}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <Kpi titulo="Por día" valor={`${d.equiposPorDia} eq.`} sub={`Te quedan ${pesos(d.gananciaPorDia)} por día`} />
                        <Kpi titulo="Pasajes" valor={pesos(d.pasajes)} plata sub={`${km(d.km)} en ${d.diasTrabajados} día${d.diasTrabajados !== 1 ? 's' : ''} (estimado)`} />
                        {sig && <Kpi titulo="Próximo tramo" valor={`Faltan ${d.faltanParaSiguiente}`} sub={`Desde ${sig.desde} equipos: ${pesos(sig.precio)} c/u`} />}
                        {d.proyeccion && d.conTarifa && <Kpi titulo="A este ritmo" valor={`${d.proyeccion.equiposEstimados} eq.`}
                            sub={`${d.proyeccion.ritmoPorDia} por día × ${d.proyeccion.diasHabilesRestantes} días hábiles que quedan`} />}
                        {d.equiposParaObjetivo != null && <Kpi titulo="Para tu objetivo" valor={`${d.equiposParaObjetivo} eq./día`}
                            sub={`Para que te queden ${pesos(d.config.objetivoDiario)} por día`} />}
                    </div>
                    <div className="p-3 rounded-2xl bg-panel">
                        <p className="text-label font-black uppercase tracking-widest text-muted mb-2">Equipos por día</p>
                        <div className="h-36">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={grafico} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.2} />
                                    <XAxis dataKey="dia" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                    <Tooltip formatter={(v, n) => (n === 'equipos' ? [v, 'Equipos'] : [v, n])} />
                                    <Bar dataKey="equipos" fill="#C9341F" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <p className="text-caption text-muted">
                        Cada día: los equipos por el precio del tramo del período (sin IVA), menos la parte del técnico (si no lo hiciste vos), los impuestos que pasan del IVA y los pasajes si fuiste en colectivo.
                        El precio puede cambiar si el período cierra en otro tramo.
                    </p>
                </div>
            )}
        </div>
    );
}
